import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';
import type { InputRounded, InputSize, InputState, InputType } from './types';

export interface InputProps
  extends ComponentProps<Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>> {
  type?: InputType;
  size?: InputSize;
  rounded?: InputRounded;
  value?: string | number;
  state?: keyof typeof InputState;
}

const input = variants({
  base: cn(
    'transition-colors-with-outline duration-300 ease-out',
    'w-full',
    'outline outline-1 outline-emphasis-subtle',
    'text-body-sm',
    'px-md py-xs',
    'bg-emphasis-default focus:bg-emphasis-strong enabled:hover:bg-emphasis-strong',
    'text-default',
    'placeholder:text-subtle',
    'resize-none',
    'disabled:cursor-not-allowed',
    'disabled:text-disabled',
    'read-only:cursor-not-allowed',
    'read-only:text-disabled',
    // Workaround for Chrome autofill forced styles that can't be overridden normally.
    // The 5000s transition delays the browser's background for a long time.
    // See: https://stackoverflow.com/q/71640408
    'autofill:[-webkit-text-fill-color:oklch(var(--text-default))]',
    'autofill:[caret-color:oklch(var(--text-default))]',
    'autofill:[-webkit-box-shadow:0_0_0_1000px_transparent_inset]',
    'autofill:[transition:background-color_5000s_ease-in-out_0s]',
  ),
  variants: {
    state: {
      default: cn(
        'focus:outline-secondary-pressed',
        'enabled:hover:focus:outline-secondary-pressed',
        'enabled:hover:outline-emphasis-stronger',
        'disabled:outline-transparent',
      ),
      error: cn('outline-error-default'),
      warning: cn('outline-warning-default'),
      success: cn('outline-success-default'),
    },
    size: {
      large: cn('leading-8'), // 40px
      xlarge: cn('leading-10'), // 48px
    },
    rounded: {
      xsmall: cn('rounded-xs'),
      small: cn('rounded-sm'),
      medium: cn('rounded-md'),
    },
  },
});

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { type, size = 'large', rounded = 'xsmall', state = 'default', ...props },
    ref,
  ) => {
    const stateClasses = input({
      state,
      rounded,
      size,
    });

    const className = getClassNameOverride(props, 'Input');
    const {
      dangerouslyOverrideClassName: _dangerouslyOverrideClassName,
      ...propsWithoutClassName
    } = props;

    return (
      <input
        aria-invalid={state === 'error'}
        className={cn(stateClasses, className)}
        type={type}
        ref={ref}
        {...propsWithoutClassName}
      />
    );
  },
);

Input.displayName = 'Input';
